"use client";

import { cn } from "@/shared/libs/commonUtils";
import {
  Calendar as CalendarDot,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { format, isEqual, parseISO } from "date-fns";
import { format as jFormat } from "date-fns-jalali";
import { enUS, faIR, type Locale } from "date-fns/locale";
import { type ElementType, type HTMLAttributes, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Calendar } from "../Calendar";
import { Button } from "../button";
import Flex from "../Flex";
import Separator from "../separator";

import Typography from "../Typography";
import Skeleton from "../Skeleton";
import style from "./style.module.css";

type Mode = "default" | "multiple" | "range" | "single" | undefined;

interface Props extends HTMLAttributes<HTMLDivElement> {
  mode?: Mode;
  OnCloseComp: ElementType;
  formatDateIntoJalali?: boolean;
  dayData?: {
    date: string;
    price?: number;
  }[];
  loading?: boolean;
  selectedDate?: DateRange;
  onPickDate: (date: { depart: string; return: string | undefined }) => void;
}

export default function Datepicker({
  mode = "single",
  OnCloseComp,
  formatDateIntoJalali = true,
  dayData,
  loading,
  selectedDate = undefined,
  onPickDate,
}: Readonly<Props>) {
  const [dateRange, setDateRange] = useState<DateRange>(
    selectedDate ?? {
      from: undefined,
      to: undefined,
    },
  );

  const [locale, setLocal] = useState<Locale>(faIR);

  const today = new Date();
  const dayBeforeToday = new Date(today);
  dayBeforeToday.setDate(today.getDate() - 1);

  const modifiers = {
    disabled: (day: Date) => {
      return day < dayBeforeToday;
    },
  };

  const handleOnDatePick = (date: DateRange | Date | undefined) => {
    if (mode === "single") {
      setDateRange((prev) => ({ ...prev, from: date as Date }));
      onPickDate({
        depart:
          formatDateIntoJalali && locale.code === "fa-IR"
            ? jFormat(date as Date, "yyyy/MM/dd")
            : format(date as Date, "yyyy/MM/dd"),
        return: undefined,
      });
    } else {
      const currentDate = date as DateRange;
      const { from, to } = currentDate;

      if (!dateRange.from) {
        setDateRange({ from, to: undefined });
      } else if (!dateRange.to) {
        setDateRange((prev) => ({ ...prev, to }));
        onPickDate({
          depart:
            formatDateIntoJalali && locale.code === "fa-IR"
              ? jFormat(from ?? "", "yyyy/MM/dd")
              : format(from ?? "", "yyyy/MM/dd"),
          return:
            formatDateIntoJalali && locale.code === "fa-IR"
              ? jFormat(to ?? "", "yyyy/MM/dd")
              : format(to ?? "", "yyyy/MM/dd"),
        });
      } else {
        setDateRange({
          from: to,
          to: undefined,
        });
      }
    }
  };

  return (
    <Flex className="flex-col">
      {/* Header */}
      <Flex className="rounded-tl-xs rounded-tr-xs justify-center items-center border-b py-2 px-3 gap-x-4">
        <Typography className="text-md font-normal">
          رفت {dateRange?.from && jFormat(new Date(dateRange.from), "d MMMM")}
        </Typography>
        {mode === "range" && dateRange?.from && (
          <Separator direction="horizontal" className="!w-4" />
        )}
        {mode === "range" && dateRange?.from && (
          <Typography className="text-md font-normal">
            بازگشت {dateRange.to && jFormat(new Date(dateRange.to), "d MMMM")}
          </Typography>
        )}
      </Flex>

      {/* Body */}
      <Flex className="p-6 justify-center max-sm:h-[80vh] overflow-y-auto">
        <Calendar
          modifiers={modifiers}
          locale={locale.code === "fa-IR" ? locale : undefined}
          mode={mode as any}
          defaultMonth={dateRange?.from}
          selected={dateRange}
          timeZone="Asia/Tehran"
          onSelect={handleOnDatePick}
          numberOfMonths={2}
          showOutsideDays={false}
          classNames={{
            nav_button_previous: style.nav_button_previous,
            nav_button_next: style.nav_button_next,
            caption_label: style.caption,
            day_range_start: "bg-foreground! rounded-tl-none! rounded-bl-none!",
            day_range_middle:
              "rounded-none! bg-muted dark:bg-muted/10! text-foreground!",
            day_range_end: "bg-foreground! rounded-tr-none! rounded-br-none!",
            months: "flex flex-col sm:flex-row gap-y-4 md:gap-x-4",
            day: cn(style.day, ""),
            day_button:
              "h-9 w-9 p-0 font-normal aria-selected:opacity-100 border-none! outline-none! ring-0! hover:border-none! hover:bg-transparent! focus:border-none! focus:outline-none! focus:ring-0! focus-visible:border-none! focus-visible:outline-none! focus-visible:ring-0!",
          }}
          components={{
            DayButton: ({ day, ...buttonProps }) => {
              const date = day.date;
              const isDayBeforeToday = dayBeforeToday > day.date;
              return !dayData ? (
                <button
                  className="flex-col items-center justify-center gap-y-1 p-1"
                  {...buttonProps}
                >
                  <Typography className="leading-none! text-sm">
                    {locale.code === "fa-IR"
                      ? jFormat(date, "d")
                      : date.getDate()}
                  </Typography>
                </button>
              ) : (
                <>
                  {loading && date ? (
                    <Flex className="flex-col items-center justify-center gap-y-1 p-1">
                      <Typography className="leading-none! text-sm">
                        {locale.code === "fa-IR"
                          ? jFormat(date, "d")
                          : date.getDate()}
                      </Typography>
                      <Skeleton className="w-2/3 h-2 rounded-xs" />
                    </Flex>
                  ) : (
                    date && (
                      <button {...buttonProps}>
                        <Flex className="flex-col items-center justify-center gap-y-1 p-1 relative">
                          {isDayBeforeToday && (
                            <Typography
                              variant="caption"
                              className="w-px h-5 rotate-50 bg-slate-500 absolute"
                            >
                              {" "}
                            </Typography>
                          )}
                          <Typography className="leading-none! font-medium">
                            {locale.code === "fa-IR"
                              ? jFormat(date, "d")
                              : date.getDate()}
                          </Typography>
                          {dayData.find((day) =>
                            isEqual(date, parseISO(day.date)),
                          )?.price && (
                            <Typography
                              variant="caption"
                              className="text-gray-400 text-[.65rem]! leading-none! font-normal"
                            >
                              {dayData
                                .find((day) =>
                                  isEqual(date, parseISO(day.date)),
                                )
                                ?.price?.toLocaleString()}
                            </Typography>
                          )}
                        </Flex>
                      </button>
                    )
                  )}
                </>
              );
            },

            Button: ({ dir, ...props }) => (
              <button
                className="shadow-none bg-transparent hover:bg-muted! dark:hover:bg-muted/10!"
                {...props}
              >
                {dir === "prev" ? (
                  <CaretLeft className="!w-5 !h-5 text-primary" />
                ) : (
                  <CaretRight className="!w-5 !h-5 text-primary" />
                )}
              </button>
            ),
          }}
        />
      </Flex>

      {/* Footer */}
      <Flex className="justify-between rounded-bl-xs rounded-br-xs border-t py-1 px-3">
        <Button
          className="rounded-xs text-xs"
          onClick={() =>
            setLocal((prev) => (prev.code === "en-US" ? faIR : enUS))
          }
          variant={"ghost"}
        >
          <Flex className="items-center gap-x-2">
            <Typography className="text-xs">
              {locale.code === "en-US" ? "تقویم شمسی" : "تقویم میلادی"}
            </Typography>
            <CalendarDot
              className={`w-5 h-5 ${
                locale.code === "en-US" ? "text-secondary" : "text-primary"
              }`}
            />
          </Flex>
        </Button>

        <OnCloseComp asChild>
          <Button className="rounded-xs text-xs" variant={"ghost"}>
            تایید
          </Button>
        </OnCloseComp>
      </Flex>
    </Flex>
  );
}
